import { apiClient } from './axios';

/**
 * Predicate is the JSON DSL — see apps/backend/src/modules/analytics/dsl/types.ts
 * Frontend treats it as opaque JSON; the JSON editor validates shape, the
 * backend re-validates by attempting to compile.
 */
export type Predicate = Record<string, unknown>;

export interface MetricDefinitionListItem {
  key: string;
  description: string;
  tags: string[];
  predicate: Predicate;
  enabled: boolean;
  source: 'code' | 'db';
  created_at?: string;
  updated_at?: string;
}

export interface MetricDefinitionDetail {
  key: string;
  description: string;
  tags: string[];
  predicate: Predicate;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateMetricDefinitionInput {
  key: string;
  description: string;
  tags?: string[];
  predicate: Predicate;
  enabled?: boolean;
}

export interface UpdateMetricDefinitionInput {
  description?: string;
  tags?: string[];
  predicate?: Predicate;
  enabled?: boolean;
}

export const metricDefinitionsApi = {
  list: async (tag?: string): Promise<MetricDefinitionListItem[]> => {
    const response = await apiClient.get<MetricDefinitionListItem[]>(
      '/analytics/metric-definitions',
      { params: tag ? { tag } : undefined },
    );
    return response.data;
  },

  getByKey: async (key: string): Promise<MetricDefinitionDetail> => {
    const response = await apiClient.get<MetricDefinitionDetail>(
      `/analytics/metric-definitions/${encodeURIComponent(key)}`,
    );
    return response.data;
  },

  create: async (data: CreateMetricDefinitionInput): Promise<MetricDefinitionDetail> => {
    const response = await apiClient.post<MetricDefinitionDetail>(
      '/analytics/metric-definitions',
      data,
    );
    return response.data;
  },

  update: async (
    key: string,
    data: UpdateMetricDefinitionInput,
  ): Promise<MetricDefinitionDetail> => {
    const response = await apiClient.put<MetricDefinitionDetail>(
      `/analytics/metric-definitions/${encodeURIComponent(key)}`,
      data,
    );
    return response.data;
  },

  delete: async (key: string): Promise<void> => {
    await apiClient.delete(`/analytics/metric-definitions/${encodeURIComponent(key)}`);
  },

  test: async (input: {
    predicate: Predicate;
    executedAtFrom?: string;
    executedAtTo?: string;
  }): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>(
      '/analytics/metric-definitions/test',
      input,
    );
    return response.data;
  },
};
